/**
 * User authentication hook for managing user state and authentication.
 * Integrates with ALB OIDC authentication via the backend /me endpoint.
 * Stage-agnostic - works with any environment through relative URLs.
 */

import { useState, useEffect, useRef } from 'react';
import { api, onUnauthenticated, UserInfo } from '../services/api';
import { preserveQueryParams } from './useQueryParams';
import { useMetrics } from './useMetrics';
import { METRICS_EVENTS } from '../constants';
import { breadcrumb } from '../utils/diagnostics';

export const useUser = () => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Guards against overlapping re-checks. The api layer already collapses a
  // burst of failed requests into one notification, but a re-check can also be
  // triggered from the UI, and two concurrent /me calls could resolve out of
  // order and clobber each other's state.
  const checkInFlight = useRef(false);
  // True between an auth-loss notification and the next successful check, so
  // the recovery metric is only attributed to an actual recovery rather than
  // to ordinary sign-in at mount.
  const recoveringFromAuthLoss = useRef(false);

  // `logEvent` reads a module-global WebSocket at call time and gets a fresh
  // identity every render, so it's held in a ref and read from inside the
  // effects — keeping it out of dependency arrays that must not re-run.
  const { logEvent } = useMetrics();
  const logEventRef = useRef(logEvent);
  logEventRef.current = logEvent;

  useEffect(() => {
    checkAuthentication();
  }, []);

  /**
   * Re-verify the session whenever an API call reports that the ALB no longer
   * recognises us.
   *
   * The ALB session cookie has an absolute lifetime measured from sign-in, not
   * a sliding one, so it expires mid-session regardless of how busy the app is
   * — an agent who signed in yesterday gets evicted partway through a live
   * contact. Nothing else re-checks auth after mount, so before this the app
   * kept believing it was signed in while every request bounced off the ALB,
   * and the shoulder-tap loop reported the service as offline instead.
   *
   * Flipping `authenticated` to false is all that is needed to recover: App
   * renders IframeAuthDialog, which drives the existing popup flow, and the
   * popup sets a fresh session cookie on our domain that this frame can then
   * use. Consumers keyed on `authenticated` (notably the shoulder-tap poll
   * loop) tear down and restart cleanly, which also clears any latched
   * offline state.
   */
  useEffect(() => onUnauthenticated(() => {
    console.warn('[auth] session lost mid-session — re-checking');
    recoveringFromAuthLoss.current = true;
    // Rides the chat WebSocket, which authenticates via SigV4 at $connect
    // rather than the ALB cookie — so an already-open socket survives the
    // cookie expiry and this usually goes out immediately. Queued if not:
    // flipping `authenticated` false unmounts the socket's owner, so the
    // recovery event that follows is emitted with no transport available.
    logEventRef.current(METRICS_EVENTS.AUTH_SESSION_LOST, { durable: true });
    checkAuthentication();
  }), []);

  const checkAuthentication = async () => {
    if (checkInFlight.current) return;
    checkInFlight.current = true;
    try {
      setLoading(true);
      setError(null);
      
      const userData = await api.getMe();
      
      if (userData?.authenticated) {
        setUser(userData);
        if (recoveringFromAuthLoss.current) {
          recoveringFromAuthLoss.current = false;
          breadcrumb('auth.recovered');
          logEventRef.current(METRICS_EVENTS.AUTH_SESSION_RECOVERED, { durable: true });
        }
        console.log(`User authenticated: ${userData.firstName || ''} ${userData.lastName || ''} (${userData.username || userData.email})`);
      } else {
        setUser(null);
        console.log('User not authenticated - login required');
      }
    } catch (err) {
      console.error('Authentication check failed:', err);
      setError(err instanceof Error ? err.message : 'Authentication failed');
      setUser(null);
    } finally {
      setLoading(false);
      checkInFlight.current = false;
    }
  };

  const login = () => {
    console.log('Initiating login flow...');
    
    // Preserve query params before redirect
    preserveQueryParams();
    
    // Trigger authentication by navigating to the dedicated auth endpoint under /api
    // ALB will handle the redirect to Cognito automatically
    // Using relative URL makes this stage-agnostic
    window.location.href = '/api/auth/login';
  };

  const logout = () => {
    console.log('Initiating logout flow...');
    // Navigate to the logout endpoint which will clear the session
    // Using relative URL makes this stage-agnostic
    window.location.href = '/api/auth/logout';
  };

  return { 
    user, 
    loading, 
    error, 
    login,
    logout, 
    checkAuthentication,
    isAuthenticated: user?.authenticated || false
  };
};
